import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { openNotification } from '../../../../helpers/notification';
import { getFacilityReport, submitReport } from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import RichtextEditor from '../../../common/RichtextEditor';

export default function ResultComments({
  open,
  onClose,
  reportId,
  facilityId,
  reportQuarter,
  reportYear,
  userId,
}) {
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState();
  const [reportLoading, setReportLoading] = useState(false);
  const [message, setMessage] = useState('');

  // console.log('report', report);
  // console.log('userId', userId);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await submitReport(reportId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.status?.desc;
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await getFacilityReport(facilityId, reportQuarter, reportYear);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setReport(data);
      }
    } catch (error) {
    } finally {
      setReportLoading(false);
    }
  };

  const handleRenderContent = () => {
    if (reportLoading) return <LoaderSkeleton />;
    if (!reportLoading && !report) return null;
    if (!reportLoading && report) return renderContent();
  };

  const renderContent = () => {
    const comments = report?.reportComments || [];

    if (comments.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No comments yet.</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
        {comments.map((i, index) => {
          const isSameUser = i?.userId === userId;
          
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: isSameUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%', // Prevents long messages from stretching edge-to-edge
              }}
            >
              {/* Sender Name */}
              <Typography
                variant="caption"
                sx={{
                  ml: isSameUser ? 'auto' : 1,
                  mr: isSameUser ? 1 : 'auto',
                  mb: 0.5,
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {isSameUser ? 'You' : i?.fullName}
              </Typography>

              {/* Chat Bubble */}
              <Box
                sx={{
                  bgcolor: isSameUser ? '#e3f2fd' : 'grey.100', // Light blue for user, grey for others
                  px: 2.5,
                  py: 1.5,
                  borderRadius: 3,
                  borderBottomRightRadius: isSameUser ? 0 : 12, // Chat bubble tail effect
                  borderBottomLeftRadius: !isSameUser ? 0 : 12,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: i?.comment }}
                  sx={{ 
                    color: 'text.primary',
                    // Removes default margins from rich text <p> tags to keep the bubble neat
                    '& p': { margin: 0 }, 
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 }
                  }}
                />
              </Box>

              {/* Timestamp */}
              <Typography
                variant="caption"
                sx={{
                  ml: isSameUser ? 'auto' : 1,
                  mr: isSameUser ? 1 : 'auto',
                  mt: 0.5,
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {new Date(report?.entryDate).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '80vh' } 
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <DialogTitle variant="h6" fontWeight={700} sx={{ p: 0 }}>
          Conversation Details
        </DialogTitle>
        <IconButton 
          onClick={() => onClose()} 
          disabled={saving}
          sx={{ color: 'text.secondary' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Content Area */}
      <DialogContent sx={{ px: 2, py: 2, bgcolor: '#fafafa' }}>
        {handleRenderContent()}
      </DialogContent>

      {/* <DialogActions sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          type="button"
          variant="contained"
          color="info"
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} />}
          sx={{ textTransform: 'none', borderRadius: 2 }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </DialogActions> 
      */}
    </Dialog>
  );
}